import {
  treeFormationName,
  type InventoryTreePackage,
  type InventoryTypeCurveTree,
  type InventoryTypeCurveTreeArea,
  type InventoryTypeCurveTreeFormation,
} from 'types/api-responses';
import type { PromoteInventoryRow } from 'types/api-payloads';
import { format0 } from 'utils/number';

/**
 * A tree leaf paired with the zone it belongs to.
 *
 * The tree renders one leaf per candidate type curve, but the id every endpoint
 * resolves on is the `(area, formation)` zone — `inventoryFormationPackageId`,
 * carried on the formation's `inventoryPackages` array, parallel to
 * `typeCurves`. Neither array alone identifies a selection, so the page works
 * with this pairing instead of a raw leaf.
 */
export interface InventoryTreeLeafRef {
  /** Stable identity for React keys and selection compares. */
  key: string;
  typeCurveArea: string;
  formation: string;
  /** Null when the zone has no package row yet — callers must guard. */
  inventoryFormationPackageId: number | null;
  uniqueTcId: string;
  typeCurveHeaderDetailsId: number | null;
  /** The id the details route resolves on. Null when the family has no promoted header. */
  tcPublicId: string | null;
  label: string;
  isDefault: boolean;
}

/**
 * The zone id a formation's leaves resolve to: the package the formation is on
 * for the type curve the tree shows on it.
 *
 * A zone holds several packages (identity is `(name, wellSpacingFt)`), and only
 * the one at the curve's spacing is the same development plan. So an operator's
 * pick wins; otherwise the default at the curve's spacing, else any package at
 * that spacing, else the default at another spacing, else the first. The same
 * rule the add/remove picker opens each row on.
 *
 * `inventoryTypeCurveId` is the pre-re-key location, read last so an older
 * backend still resolves.
 *
 * @param {InventoryTypeCurveTreeFormation} formation - The formation node.
 * @param {number | null | undefined} curveSpacingFt - The shown curve's spacing.
 * @param {number | undefined} legacyId - The leaf's deprecated zone id.
 * @return {number | null} The zone id, or null when the zone has no package.
 */
export const zoneIdFor = (
  formation: InventoryTypeCurveTreeFormation,
  curveSpacingFt?: number | null,
  legacyId?: number
): number | null => {
  const packages = formation.inventoryPackages ?? [];
  const picked = packages.find((pkg) => pkg.isPicked);
  if (picked) return picked.inventoryFormationPackageId;

  const spacing = curveSpacingFt ?? null;
  const atSpacing =
    spacing === null
      ? []
      : packages.filter((pkg) => pkg.wellSpacingFt === spacing);
  const pool = atSpacing.length > 0 ? atSpacing : packages;
  const chosen = pool.find((pkg) => pkg.isDefault) ?? pool[0];
  return chosen?.inventoryFormationPackageId ?? legacyId ?? null;
};

/**
 * A leaf ref's key. The zone alone is not unique — a zone holds several type
 * curves — so the family is part of it.
 *
 * @param {number | null} zoneId - The leaf's `inventoryFormationPackageId`.
 * @param {string} uniqueTcId - The leaf's type curve family.
 * @return {string} The stable key.
 */
const treeLeafKey = (zoneId: number | null, uniqueTcId: string): string =>
  `${zoneId ?? 'none'}:${uniqueTcId}`;

/**
 * The same leaf on another of its formation's packages — what clicking a
 * package node in the tree selects. Re-keyed exactly as `resolveTreeLeaves`
 * keys it once that package is the formation's pick, so the selection and the
 * rebuilt tree agree.
 *
 * @param {InventoryTreeLeafRef} leaf - A leaf of the package's formation.
 * @param {number} inventoryFormationPackageId - The chosen package.
 * @return {InventoryTreeLeafRef} The leaf keyed on that package.
 */
export const withTreePackage = (
  leaf: InventoryTreeLeafRef,
  inventoryFormationPackageId: number
): InventoryTreeLeafRef => ({
  ...leaf,
  key: treeLeafKey(inventoryFormationPackageId, leaf.uniqueTcId),
  inventoryFormationPackageId,
});

/**
 * A package's tree label. A seed package is created with no name, so it reads
 * by its spacing rather than as a blank row.
 *
 * @param {InventoryTreePackage} pkg - The formation's package.
 * @return {string} The label.
 */
export const treePackageLabel = (pkg: InventoryTreePackage): string => {
  if (pkg.displayName) return pkg.displayName;
  if (pkg.name) return pkg.name;
  return pkg.wellSpacingFt !== null
    ? `New package — ${format0(pkg.wellSpacingFt)} ft`
    : 'New package';
};

/**
 * The server's default package of each formation. Read off the response, not
 * the rendered tree, where a picked package is flagged as the formation's pick.
 *
 * @param {InventoryTypeCurveTree | undefined} tree - The response's tree.
 * @return {ReadonlySet<number>} Default `inventoryFormationPackageId`s.
 */
export const defaultTreePackageIds = (
  tree: InventoryTypeCurveTree | undefined
): ReadonlySet<number> =>
  new Set(
    (tree ? resolveTreePackages(tree) : [])
      .filter((pkg) => pkg.isDefault)
      .map((pkg) => pkg.inventoryFormationPackageId)
  );

/**
 * The tree's package picks that outlive an add/remove apply: a zone still
 * included whose package the picker did not report. A package the picker
 * reports is the later choice and replaces the tree's.
 *
 * @param {Record<string, number>} picks - Zone id → tree-picked package id.
 * @param {ReadonlySet<string>} included - Zone ids still in the tree.
 * @param {UpdatedContextRow[]} rows - The picker's reported rows.
 * @return {Record<string, number>} The picks to keep.
 */
export const retainTreePackagePicks = (
  picks: Record<string, number>,
  included: ReadonlySet<string>,
  rows: readonly UpdatedContextRow[]
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(picks).filter(([zoneId]) => {
      const reported = rows.find((row) => row.rowId === zoneId)?.package;
      return (
        included.has(zoneId) &&
        (reported?.inventoryFormationPackageId ?? null) === null
      );
    })
  );

/**
 * The package each formation is on — the one its leaves resolve to.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {ReadonlySet<number>} The in-use `inventoryFormationPackageId`s.
 */
export const treePackagesInUse = (
  tree: InventoryTypeCurveTree
): ReadonlySet<number> =>
  new Set(
    tree.areas.flatMap((area) =>
      area.formations
        .map((formation) =>
          zoneIdFor(formation, formation.typeCurves[0]?.wellSpacingFt)
        )
        .filter((zoneId): zoneId is number => zoneId !== null)
    )
  );

/**
 * How many packages the tree lists, across every formation.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {number} The package count.
 */
export const treePackageCount = (tree: InventoryTypeCurveTree): number =>
  tree.areas.reduce(
    (sum, area) =>
      sum +
      area.formations.reduce(
        (count, formation) => count + treeInventoryPackages(formation).length,
        0
      ),
    0
  );

/**
 * The orphan UNKNOWN zone is a storage bucket for no-formation wells, not a real
 * type-curve formation (created with no name and no spacing). It offers no
 * inventory selection and must not gate Promote. Matches the unit builder's own
 * `fmt === 'UNKNOWN'` guard.
 * @param {string} formation - The zone's formation.
 * @return {boolean} Whether the zone is the UNKNOWN bucket.
 */
export const isUnknownZone = (formation: string): boolean =>
  formation.trim().toUpperCase() === 'UNKNOWN';

/**
 * The inventories a formation lists in the tree. The UNKNOWN bucket's zone is
 * not an inventory anybody chooses, so it lists none and is not counted; the
 * formation node itself still shows.
 * @param {InventoryTypeCurveTreeFormation} formation - The formation node.
 * @return {InventoryTreePackage[]} The packages to list.
 */
export const treeInventoryPackages = (
  formation: InventoryTypeCurveTreeFormation
): InventoryTreePackage[] =>
  isUnknownZone(treeFormationName(formation))
    ? []
    : (formation.inventoryPackages ?? []);

/**
 * Flatten the workspace tree to leaf refs, in the order the tree renders them.
 *
 * One list drives the left-nav keys, the page's default selection and the
 * builder's formation stepper, so all three agree on order and identity.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {InventoryTreeLeafRef[]} Leaf refs in render order.
 */
export const resolveTreeLeaves = (
  tree: InventoryTypeCurveTree
): InventoryTreeLeafRef[] =>
  tree.areas.flatMap((area) =>
    area.formations.flatMap((formation) => {
      const formationName = treeFormationName(formation);
      return formation.typeCurves.map((leaf) => {
        const zoneId = zoneIdFor(
          formation,
          leaf.wellSpacingFt,
          leaf.inventoryTypeCurveId
        );
        return {
          key: treeLeafKey(zoneId, leaf.uniqueTcId),
          typeCurveArea: area.typeCurveArea,
          formation: formationName,
          inventoryFormationPackageId: zoneId,
          uniqueTcId: leaf.uniqueTcId,
          typeCurveHeaderDetailsId: leaf.typeCurveHeaderDetailsId,
          tcPublicId: leaf.tcPublicId ?? null,
          label: leaf.label,
          isDefault: leaf.isDefault,
        };
      });
    })
  );

/**
 * Every zone of the workspace, as promote/plan request rows.
 *
 * A promote targets zones, not type curves, and omitting `inventories[]` makes
 * the server fall back to previewing all of them with their stored names — so
 * the rows are always sent, and the plan and the promote stay in step.
 *
 * `setAsDefault` is fanned out from the modal's single checkbox: the server
 * takes the flag per zone, but the UI offers one tick, so every row carries the
 * same value. Splitting it per zone is a UI change, not a payload one.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @param {boolean} setAsDefault - The modal's default checkbox.
 * @return {PromoteInventoryRow[]} One row per zone.
 */
export const resolveTreeZones = (
  tree: InventoryTypeCurveTree,
  setAsDefault: boolean
): PromoteInventoryRow[] =>
  tree.areas.flatMap((area) =>
    area.formations.flatMap((formation) =>
      (formation.inventoryPackages ?? []).map((pkg) => ({
        inventoryFormationPackageId: pkg.inventoryFormationPackageId,
        formation: treeFormationName(formation),
        inventoryPackageName: pkg.name,
        wellSpacingFt: pkg.wellSpacingFt,
        setAsDefault,
      }))
    )
  );

/** One zone's package, with the area and formation naming it. */
export interface TreeZonePackage {
  inventoryFormationPackageId: number;
  typeCurveArea: string;
  formation: string;
  name: string | null;
  wellSpacingFt: number | null;
  displayName: string | null;
  /** The per-formation default package. */
  isDefault: boolean;
}

/**
 * Every zone package the workspace covers, in tree order.
 *
 * The promote modal picks what each zone publishes as and whether it takes its
 * default, so it needs the zones themselves rather than a prebuilt payload —
 * `resolveTreeZones` fans one shared flag across them, which is the pre-v14
 * single-checkbox shape.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {TreeZonePackage[]} One entry per zone package.
 */
export const resolveTreePackages = (
  tree: InventoryTypeCurveTree
): TreeZonePackage[] =>
  tree.areas.flatMap((area) =>
    area.formations.flatMap((formation) =>
      (formation.inventoryPackages ?? []).map((pkg) => ({
        inventoryFormationPackageId: pkg.inventoryFormationPackageId,
        typeCurveArea: area.typeCurveArea,
        formation: treeFormationName(formation),
        name: pkg.name,
        wellSpacingFt: pkg.wellSpacingFt,
        displayName: pkg.displayName,
        isDefault: pkg.isDefault,
      }))
    )
  );

/**
 * The composite id a zone is keyed by in the add/remove picker.
 *
 * Matches the candidates row id (`"{typeCurveArea}:{formation}"`), so the tree
 * and the picker's checkboxes line up without a translation table.
 *
 * @param {string} areaName - Type curve area.
 * @param {string} formation - Formation.
 * @return {string} The composite id.
 */
export const treeZoneKey = (areaName: string, formation: string): string =>
  `${areaName}:${formation}`;

/**
 * Zone ids the tree currently covers — what the picker opens on.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {string[]} Composite `"<area>:<formation>"` ids.
 */
export const resolveTreeZoneKeys = (tree: InventoryTypeCurveTree): string[] =>
  tree.areas.flatMap((area) =>
    area.formations.map((formation) =>
      treeZoneKey(area.typeCurveArea, treeFormationName(formation))
    )
  );

/**
 * The tree narrowed to the included zones.
 *
 * Frontend-only: the response is left untouched and this derives what the
 * left-nav shows, so a reload returns to the server's own set. Areas left with
 * no formation drop out, and the counts are recomputed rather than carried over
 * — a stale `typeCurveCount` would contradict the visible rows.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @param {ReadonlySet<string> | null} included - Zone ids to keep; null keeps all.
 * @return {InventoryTypeCurveTree} The narrowed tree.
 */
export const filterTreeByZones = (
  tree: InventoryTypeCurveTree,
  included: ReadonlySet<string> | null
): InventoryTypeCurveTree => {
  if (!included) return tree;

  const areas = tree.areas
    .map((area) => {
      const formations = area.formations.filter((formation) =>
        included.has(
          treeZoneKey(area.typeCurveArea, treeFormationName(formation))
        )
      );
      const uniqueTcIds = new Set(
        formations.flatMap((formation) =>
          formation.typeCurves.map((leaf) => leaf.uniqueTcId)
        )
      );
      return { ...area, formations, typeCurveCount: uniqueTcIds.size };
    })
    .filter((area) => area.formations.length > 0);

  const total = new Set(
    areas.flatMap((area) =>
      area.formations.flatMap((formation) =>
        formation.typeCurves.map((leaf) => leaf.uniqueTcId)
      )
    )
  );

  return { ...tree, areas, typeCurveCount: total.size };
};

/**
 * The tree reduced to ONE type curve per formation — the selected one.
 *
 * The backend lists every current-promoted candidate for a `(area, formation)`,
 * but a formation carries a single type curve. The selection, in order: the
 * `isDefault` leaf, else the `isOrigin` (launched) leaf, else the first. Area and
 * tree counts are recomputed so the header/area numbers match the
 * one-per-formation rows, not the candidate totals.
 *
 * Frontend-only, like the rest of the tree transforms: the response is left
 * untouched, so a reload returns the server's full set. Apply this BEFORE
 * `applyTypeCurvePicks`, so an operator's pick still replaces the selected leaf.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {InventoryTypeCurveTree} The tree with one curve per formation.
 */
export const selectTypeCurvePerFormation = (
  tree: InventoryTypeCurveTree
): InventoryTypeCurveTree => {
  const areas = tree.areas.map((area) => {
    const formations = area.formations.map((formation) => {
      const curves = formation.typeCurves;
      const selected =
        curves.find((leaf) => leaf.isDefault) ??
        curves.find((leaf) => leaf.isOrigin) ??
        curves[0];
      return { ...formation, typeCurves: selected ? [selected] : [] };
    });
    const typeCurveCount = formations.filter(
      (formation) => formation.typeCurves.length > 0
    ).length;
    return { ...area, formations, typeCurveCount };
  });

  const typeCurveCount = areas.reduce(
    (sum, area) => sum + area.typeCurveCount,
    0
  );

  return { ...tree, areas, typeCurveCount };
};

/** A zone's chosen type curve, keyed by `"<area>:<formation>"`. */
export interface TreeTypeCurvePick {
  uniqueTcId: string;
  label: string;
  typeCurveHeaderDetailsId: number | null;
  /**
   * The id `/type_curve_headers/:public_id` resolves on.
   *
   * Null from the add/remove picker: the candidates endpoint does not emit
   * `tc_public_id` (the tree does). Carrying the replaced leaf's would be worse
   * than null — it would load a different curve's header. Until candidates
   * emits it, a picked curve shows in the tree but its Type Curve panel cannot
   * resolve.
   */
  tcPublicId?: string | null;
  /** The curve's well spacing, which the formation's package follows. */
  wellSpacingFt?: number | null;
}

/**
 * The full resolved selection for one included row of the Add/Remove picker.
 *
 * The picker reports one of these per checked row (not just changes), so the
 * page can rebuild a sidebar leaf even for a zone the workspace wasn't created
 * with. `package` is null when the zone has no promoted package yet.
 */
export interface UpdatedContextRow {
  /** `"{typeCurveArea}:{formation}"` — matches `treeZoneKey`. */
  rowId: string;
  typeCurveArea: string;
  formation: string;
  typeCurve: TreeTypeCurvePick;
  package: {
    /**
     * The zone id the pick re-keys the selected leaf to. Null for an
     * add-candidate row the workspace doesn't own yet (no zone exists to view).
     */
    inventoryFormationPackageId: number | null;
    name: string;
    displayName: string;
    wellSpacingFt: number | null;
  } | null;
}

/**
 * Grow the tree with zones the operator checked that the workspace wasn't
 * created with — built from the picker's candidate-derived context. Candidate
 * packages carry no id, so a synthetic zone gets an empty `inventoryPackages`
 * (leaf `inventoryFormationPackageId` resolves to null); its Type Curve sections
 * still load via the header fetch. Zones already in the tree are left untouched.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @param {UpdatedContextRow[]} rows - Included-row context from the picker.
 * @return {InventoryTypeCurveTree} The tree with synthetic zones appended.
 */
export const mergeContextZones = (
  tree: InventoryTypeCurveTree,
  rows: readonly UpdatedContextRow[]
): InventoryTypeCurveTree => {
  if (rows.length === 0) return tree;
  const existing = new Set(resolveTreeZoneKeys(tree));
  const toAdd = rows.filter((row) => !existing.has(row.rowId));
  if (toAdd.length === 0) return tree;

  // Clone the formation arrays so appending doesn't mutate the response.
  const areas: InventoryTypeCurveTreeArea[] = tree.areas.map((area) => ({
    ...area,
    formations: [...area.formations],
  }));
  const byName = new Map(areas.map((area) => [area.typeCurveArea, area]));

  toAdd.forEach((row) => {
    let area = byName.get(row.typeCurveArea);
    if (!area) {
      area = {
        typeCurveArea: row.typeCurveArea,
        isOrigin: false,
        typeCurveCount: 0,
        formations: [],
      };
      byName.set(row.typeCurveArea, area);
      areas.push(area);
    }
    area.formations.push({
      formation: row.formation,
      typeCurves: [
        {
          uniqueTcId: row.typeCurve.uniqueTcId,
          label: row.typeCurve.label,
          typeCurveHeaderDetailsId: row.typeCurve.typeCurveHeaderDetailsId,
          tcPublicId: row.typeCurve.tcPublicId ?? null,
          isDefault: true,
          wellSpacingFt: row.typeCurve.wellSpacingFt ?? null,
        },
      ],
      inventoryPackages: [],
    });
  });

  const withCounts = areas.map((area) => ({
    ...area,
    typeCurveCount: new Set(
      area.formations.flatMap((formation) =>
        formation.typeCurves.map((leaf) => leaf.uniqueTcId)
      )
    ).size,
  }));
  const total = new Set(
    withCounts.flatMap((area) =>
      area.formations.flatMap((formation) =>
        formation.typeCurves.map((leaf) => leaf.uniqueTcId)
      )
    )
  );
  return { ...tree, areas: withCounts, typeCurveCount: total.size };
};

/**
 * The tree with each picked zone showing the type curve the operator chose.
 *
 * The response carries exactly ONE curve per zone — the one the workspace was
 * created with — while the picker offers every current-promoted candidate for
 * that `(area, formation)`. So a pick cannot be satisfied by reordering the
 * leaf list; the leaf is substituted outright.
 *
 * Frontend-only, like the rest of add/remove: no endpoint accepts a chosen
 * curve, so a reload returns to the server's own set.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @param {Record<string, TreeTypeCurvePick>} picks - Chosen curve per zone id.
 * @return {InventoryTypeCurveTree} The tree with picked leaves substituted.
 */
export const applyTypeCurvePicks = (
  tree: InventoryTypeCurveTree,
  picks: Record<string, TreeTypeCurvePick>
): InventoryTypeCurveTree => {
  if (Object.keys(picks).length === 0) return tree;

  return {
    ...tree,
    areas: tree.areas.map((area) => ({
      ...area,
      formations: area.formations.map((formation) => {
        const pick =
          picks[treeZoneKey(area.typeCurveArea, treeFormationName(formation))];
        if (!pick) return formation;

        // isDefault follows the leaf it replaces: the tag reflects the zone's
        // default type curve, which the pick does not change.
        const replaced = formation.typeCurves[0];

        // Re-selecting the curve already shown is a no-op. Substituting it would
        // still null the tcPublicId below and take the pick's label, collapsing a
        // promoted leaf to the "no promoted header" state and relabelling it. The
        // add/remove candidates label the launched curve by the workspace name and
        // omit tcPublicId, so the picker reports the (locked) origin row as a pick
        // even when nothing changed — this keeps that from clobbering the leaf.
        if (pick.uniqueTcId === replaced?.uniqueTcId) return formation;

        return {
          ...formation,
          typeCurves: [
            {
              uniqueTcId: pick.uniqueTcId,
              label: pick.label,
              typeCurveHeaderDetailsId: pick.typeCurveHeaderDetailsId,
              tcPublicId: pick.tcPublicId ?? null,
              isDefault: replaced?.isDefault ?? false,
              wellSpacingFt: pick.wellSpacingFt ?? null,
            },
            ...formation.typeCurves.slice(1),
          ],
        };
      }),
    })),
  };
};

/**
 * The tree with each picked zone showing the inventory package the operator
 * chose. Marks the picked package (matched by `inventoryFormationPackageId`)
 * `isPicked`, which `zoneIdFor` resolves ahead of every default — re-keying the
 * leaf so the selected-row detail fetch fires. The order and the server's
 * `isDefault` are left alone, so the tree does not reshuffle on a click and the
 * Default tag stays on the real default. Keying on the id, not the
 * `displayName`, is load-bearing: the picker's launch candidates can carry the
 * same package name at a different spacing, so the strings would not match the
 * zone's. No-op for a zone whose picked package isn't in the tree (a synthetic
 * zone with no packages).
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @param {Record<string, number>} packagePicks - Zone id → the package's `inventoryFormationPackageId`.
 * @return {InventoryTypeCurveTree} The tree with picked packages marked.
 */
export const applyPackagePicks = (
  tree: InventoryTypeCurveTree,
  packagePicks: Record<string, number>
): InventoryTypeCurveTree => {
  if (Object.keys(packagePicks).length === 0) return tree;

  return {
    ...tree,
    areas: tree.areas.map((area) => ({
      ...area,
      formations: area.formations.map((formation) => {
        const pick =
          packagePicks[
            treeZoneKey(area.typeCurveArea, treeFormationName(formation))
          ];
        if (pick === undefined) return formation;
        const packages = formation.inventoryPackages ?? [];
        if (!packages.some((pkg) => pkg.inventoryFormationPackageId === pick)) {
          return formation;
        }
        return {
          ...formation,
          inventoryPackages: packages.map((pkg) => ({
            ...pkg,
            isPicked: pkg.inventoryFormationPackageId === pick,
          })),
        };
      }),
    })),
  };
};

/** The zone selections the left nav currently shows. */
export interface TreeSelections {
  /** Zone id → the curve's `uniqueTcId`. */
  typeCurves: Record<string, string>;
  /** Zone id → the package's `inventoryFormationPackageId`. */
  packages: Record<string, number>;
}

/**
 * What the left nav shows right now, keyed by zone.
 *
 * The picker opens on this rather than on the candidates' own defaults: in edit
 * mode the question is "what is this workspace using", and the answer lives in
 * the tree. The response carries one curve per zone, so the first leaf IS the
 * current selection.
 *
 * @param {InventoryTypeCurveTree} tree - The workspace's type curve tree.
 * @return {TreeSelections} Current curve and package per zone.
 */
export const resolveTreeSelections = (
  tree: InventoryTypeCurveTree
): TreeSelections => {
  const typeCurves: Record<string, string> = {};
  const packages: Record<string, number> = {};

  tree.areas.forEach((area) => {
    area.formations.forEach((formation) => {
      const zoneId = treeZoneKey(
        area.typeCurveArea,
        treeFormationName(formation)
      );

      const curve = formation.typeCurves[0];
      if (curve) typeCurves[zoneId] = curve.uniqueTcId;

      // The package the formation is on, by the same rule the leaves resolve.
      const chosen = zoneIdFor(formation, curve?.wellSpacingFt);
      if (chosen !== null) packages[zoneId] = chosen;
    });
  });

  return { typeCurves, packages };
};
